import { AnyObject, Params, PlatformApp, WithPagination } from '@typings';
import { API_APPS, API_APPS_V2, JWT_TOKEN } from '@constants';
import { axios } from '@features';
import { getFormattedApiUrl, localStorage, path } from '@utils';

/**
 * Helper
 */
const getApiUrl = (context: Params.Context) =>
  getFormattedApiUrl({ ...context, apiUrl: API_APPS, plural: false });

const processAppsMonitoringUrl = (url: string) => url.replace('jobs', 'apps');

export const getPlatformAppsTemplates = (
  {
    clusterName,
    organizationName,
    projectName,
    types,
    inputTypes,
    outputTypes,
  }: Params.GetPlatformAppsTemplates,
  options: { formatResponse: boolean } = { formatResponse: false },
) => {
  const url = getApiUrl({ clusterName, organizationName, projectName });

  const params: Record<string, string> = {};

  if (types) {
    params.types = types.join(',');
  }

  if (inputTypes) {
    params.input_types = inputTypes.join(',');
  }

  if (outputTypes) {
    params.output_types = outputTypes.join(',');
  }

  return axios.get<never, PlatformApp.Template[]>(
    path.create(url, 'templates'),
    {
      params,
      formatResponse: options.formatResponse,
    },
  );
};
export const getPlatformAppsInstances = ({
  clusterName,
  organizationName,
  projectName,
}: Params.GetPlatformAppsInstances) => {
  const url = getApiUrl({ clusterName, organizationName, projectName });

  return axios.get<never, WithPagination<PlatformApp.Instance>>(
    path.create(url, 'instances'),
    /**
     * Temporarly added until pagination will be implemented
     */
    { params: { size: 100 } },
  );
};

export const getPlatformAppsInstance = ({
  instanceId,
}: Params.GetPlatformAppsInstance) => {
  return axios.get<never, PlatformApp.Instance>(
    path.create(API_APPS_V2, 'instances', instanceId),
  );
};

export const installPlatformApp = ({
  clusterName,
  organizationName,
  projectName,
  payload,
}: Params.InstallPlatformApp) => {
  const url = getApiUrl({ clusterName, organizationName, projectName });

  return axios.post<never, PlatformApp.Instance>(
    path.create(url, 'instances'),
    payload,
  );
};

export const uninstallPlatformApp = ({
  clusterName,
  organizationName,
  projectName,
  id,
}: Params.UninstallPlatformApp) => {
  const url = getApiUrl({
    clusterName,
    organizationName,
    projectName,
  });

  return axios.delete<never>(path.create(url, 'instances', id));
};

export const getPlatformAppVersions = ({
  organizationName,
  clusterName,
  projectName,
  appName,
}: Params.GetPlatformAppVersions) => {
  const url = getApiUrl({
    clusterName,
    organizationName,
    projectName,
  });

  return axios.get<never, PlatformApp.Template[]>(
    path.create(url, 'templates', appName),
    { formatResponse: false },
  );
};

export const getPlatformAppLog = ({
  instanceId,
  monitoringUrl,
  since = '',
}: Params.GetPlatformAppLog) => {
  const token = localStorage.get(JWT_TOKEN);
  const params = new URLSearchParams({
    prefix: 'true',
    since,
  });

  return fetch(
    path.create(
      processAppsMonitoringUrl(monitoringUrl),
      instanceId,
      `log?${params.toString()}`,
    ),
    { headers: { Authorization: `Bearer ${token}` } },
  );
};

export const getPlatformAppLogContainers = ({
  instanceId,
  monitoringUrl,
}: Params.GetPlatformAppLogContainers) => {
  type Container = string;

  return axios.get<never, Container[]>(
    path.create(
      processAppsMonitoringUrl(monitoringUrl),
      instanceId,
      'containers',
    ),
  );
};

export const getPlatformAppInstnceParams = ({
  organizationName,
  clusterName,
  projectName,
  instanceId,
  type,
}: Params.GetPlatformAppInstnceParams) => {
  const url = getApiUrl({
    clusterName,
    organizationName,
    projectName,
  });

  return axios.get<never, AnyObject>(
    path.create(url, 'instances', instanceId, type),
    { formatResponse: false },
  );
};
